import { useState } from "react";
import { useLocation } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ExpandCollapseButton } from "@/components/ui/expand-collapse-button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import {
  Activity,
  Lock,
  FileWarning,
  MailWarning,
  Mail,
  ExternalLink,
  Unlock,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  XCircle,
  PawPrint,
  Percent,
  ChevronDown,
  ChevronUp,
  X,
  Camera,
} from "lucide-react";
import { Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";

interface RateLimitEntry {
  type: string;
  key: string;
  lockedUntil: string;
  remainingSeconds: number;
}

interface RateLimitResponse {
  securityMode: string;
  lockedCount: number;
  lockedEntries: RateLimitEntry[];
  rateLimitConfigs: Record<string, { maxAttempts: number; windowMinutes: number; lockoutMinutes: number }>;
}

interface PendingReportItem {
  id: string;
  sourceReportType: string;
  sourceReportId: string;
  reviewStatus: string;
  reviewReason: string | null;
  parsedData: {
    location?: string;
    reportDate?: string;
  };
  createdAt: string;
  sourceEmailId?: string | null;
}

interface PendingReportsResponse {
  items: PendingReportItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface DiagnosticMismatch {
  reportId: string;
  reportType: "all_pets" | "interaction";
  reportDate: string | null;
  location: string | null;
  mismatches: string[];
}

interface IngestHealthData {
  lookbackDays: number;
  cutoff: string;
  summary: {
    total: number;
    processed: number;
    failed: number;
    needsReview: number;
    pending: number;
  };
  byClassification: {
    behaviorAlerts: number;
    allPets: number;
    interactions: number;
    unknown: number;
  };
  failures: {
    id: string;
    fromAddress: string | null;
    subject: string | null;
    classification: string;
    processingStatus: string;
    processingError: string | null;
    receivedAt: string;
    gatewayDecision: string | null;
    gatewayReason: string | null;
  }[];
  diagnosticMismatches: DiagnosticMismatch[];
}

interface QualityFlagSummary {
  noteQuality: { emptyNote: number; scoreOnly: number; shortReport: number };
  dataIntegrity: { phantom: number; contaminatedTail: number; crossAnimalBleed: number; unsuperseded: number; brokenChain: number; bloatedRawNote: number };
}

const DATA_INTEGRITY_ITEMS: { key: keyof QualityFlagSummary["dataIntegrity"]; label: string; description: string }[] = [
  { key: "phantom",          label: "Phantom record",       description: "Email alert parsed as a block header with no usable note content" },
  { key: "contaminatedTail", label: "Contaminated note",    description: "Real note body has block-header text appended by the parser" },
  { key: "crossAnimalBleed", label: "Cross-animal content", description: "Note body contains another animal's ID or evaluation block" },
  { key: "unsuperseded",     label: "Email not superseded", description: "Email alert still canonical alongside an accepted PDF report (>3 days)" },
  { key: "brokenChain",      label: "Broken chain",         description: "Superseded record points to another superseded record, not the canonical" },
  { key: "bloatedRawNote",   label: "Duplicate note content", description: "Score field text repeated inside the note body (CI-4 regression guard)" },
];

interface SystemInfo {
  version: string;
  buildHash: string;
  environment: string;
  securityMode: string;
  apiReachable: boolean;
  database: {
    healthy: boolean;
    error: string | null;
  };
  email: {
    configured: boolean;
    isProduction: boolean;
  };
  timestamp: string;
}

type HealthLevel = "operational" | "attention" | "critical";

interface ProfileScanAlert {
  code: "stale_run" | "http_errors" | "photo_rate_drop" | "profile_rate_drop" | "bio_rate_drop";
  severity: "attention" | "critical";
  message: string;
  detail?: string;
}

interface ProfileScanHealth {
  lastRunAt: string | null;
  lastRunHour: string | null;
  lastRunAnimalCount: number | null;
  lastRunHttp200: number | null;
  lastRunHttp404: number | null;
  lastRunErrorCount: number | null;
  lastRunPhotoFoundCount: number | null;
  lastRunBioFoundCount: number | null;
  alertCount: number;
  criticalCount: number;
  alerts: ProfileScanAlert[];
}

function deriveHealthLevel(
  systemInfo: SystemInfo | undefined,
  systemInfoError: boolean,
  lockoutCount: number,
  failedIngests: number,
  pendingReviewCount: number,
  dataIntegrityCount: number,
  scanCriticalCount: number,
  scanAlertCount: number,
): HealthLevel {
  if (systemInfoError) return "critical";
  if (systemInfo && (!systemInfo.database.healthy || !systemInfo.apiReachable)) return "critical";
  if (scanCriticalCount > 0) return "critical";
  if (lockoutCount > 0 || failedIngests > 0 || pendingReviewCount > 0 || dataIntegrityCount > 0 || scanAlertCount > 0) return "attention";
  return "operational";
}

function getHealthLabel(level: HealthLevel): string {
  if (level === "critical") return "System Issue Detected";
  if (level === "attention") return "Attention Needed";
  return "All Systems Go";
}

function getHealthIcon(level: HealthLevel) {
  if (level === "critical") return AlertCircle;
  if (level === "attention") return AlertTriangle;
  return Activity;
}

function getHealthIconColor(level: HealthLevel): string {
  if (level === "critical") return "text-destructive";
  if (level === "attention") return "text-amber-600 dark:text-amber-500";
  return "text-primary";
}

function getHealthBgColor(level: HealthLevel): string {
  if (level === "critical") return "bg-destructive/10";
  if (level === "attention") return "bg-amber-500/10";
  return "bg-primary/10";
}

function getHealthBorderColor(level: HealthLevel): string {
  if (level === "critical") return "border-red-300 dark:border-red-700";
  if (level === "attention") return "border-amber-300 dark:border-amber-700";
  return "border-emerald-300 dark:border-emerald-700";
}

function getHealthCardBg(_level: HealthLevel): string {
  return "";
}

interface AdminCalloutsProps {
  totalAnimals: number;
  avgCapacityPercent: number | null;
  capacityStatus: "ok" | "near" | "over" | "unknown";
  isAdmin: boolean;
  isSuperAdmin: boolean;
  onCollapse?: () => void;
  highlightClassName?: string;
}

function getCapacityColor(status: string): string {
  if (status === "over") return "text-destructive";
  if (status === "near") return "text-amber-600 dark:text-amber-500";
  return "text-emerald-600 dark:text-emerald-500";
}

function getCapacityLabel(status: string): string {
  if (status === "over") return "Over";
  if (status === "near") return "Near";
  if (status === "ok") return "OK";
  return "";
}

function formatRemainingTime(seconds: number): string {
  if (seconds <= 0) return "Expired";
  if (seconds < 60) return `${seconds}s`;
  return `${Math.ceil(seconds / 60)} min`;
}

function parseEmailIngestHoldReason(reason: string | null): {
  isEmailIngestHold: boolean;
  description: string | null;
} {
  if (!reason) return { isEmailIngestHold: false, description: null };
  if (reason.startsWith("EMAIL_INGEST_HOLD:")) {
    const afterColon = reason.substring("EMAIL_INGEST_HOLD:".length).trim();
    return { isEmailIngestHold: true, description: afterColon || null };
  }
  if (reason.includes("EMAIL_INGEST_HOLD")) {
    const parts = reason.split(";").map(s => s.trim());
    const otherParts = parts.filter(p => !p.includes("EMAIL_INGEST_HOLD"));
    return {
      isEmailIngestHold: true,
      description: otherParts.length > 0 ? otherParts.join("; ") : null,
    };
  }
  return { isEmailIngestHold: false, description: reason };
}

function parseInteractionDiagnostic(reason: string | null): {
  isScoreDiagnostic: boolean;
  count: number;
  animalIds: string[];
} {
  if (!reason) return { isScoreDiagnostic: false, count: 0, animalIds: [] };
  const scoreMatch = reason.match(/(\d+)\s+interaction\(s\)\s+have\s+no\s+score:\s*(.+)/i);
  if (scoreMatch) {
    const count = parseInt(scoreMatch[1], 10);
    const animalIds = scoreMatch[2].split(",").map(id => id.trim()).filter(Boolean);
    return { isScoreDiagnostic: true, count, animalIds };
  }
  return { isScoreDiagnostic: false, count: 0, animalIds: [] };
}

function getReportTypeLabel(type: string): string {
  if (type === "all_pets") return "All Pets Report";
  if (type === "interaction") return "Daily Interactions Summary";
  return type;
}

function getReviewUrl(report: PendingReportItem): string {
  if (report.sourceReportType === "interaction") {
    return `/reports/review/${report.sourceReportType}/${report.sourceReportId}`;
  }
  return `/reports/review/${report.sourceReportType}/${report.id}`;
}

export function AdminCallouts({
  totalAnimals,
  avgCapacityPercent,
  capacityStatus,
  isAdmin,
  isSuperAdmin,
  onCollapse,
  highlightClassName,
}: AdminCalloutsProps) {
  const [lockoutSheetOpen, setLockoutSheetOpen] = useState(false);
  const [reviewSheetOpen, setReviewSheetOpen] = useState(false);
  const [ingestSheetOpen, setIngestSheetOpen] = useState(false);
  const [profileScanSheetOpen, setProfileScanSheetOpen] = useState(false);
  const [expandedFailureId, setExpandedFailureId] = useState<string | null>(null);
  const [dataIntegrityExpanded, setDataIntegrityExpanded] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const { data: systemInfo, isError: systemInfoError } = useQuery<SystemInfo>({
    queryKey: ["/api/settings/system-info"],
    enabled: isAdmin,
    refetchInterval: 60000,
  });

  const { data: rateLimits, isLoading: lockoutsLoading } = useQuery<RateLimitResponse>({
    queryKey: ["/api/admin/rate-limits"],
    enabled: isAdmin,
  });

  const { data: pendingReportsData, isLoading: pendingLoading } = useQuery<PendingReportsResponse>({
    queryKey: ["/api/reports/pending?status=pending&limit=50"],
    enabled: isAdmin,
  });

  const { data: ingestHealth, isLoading: ingestLoading } = useQuery<IngestHealthData>({
    queryKey: ["/api/dashboard/ingest-health"],
    enabled: isSuperAdmin,
  });

  const { data: qualityFlags, isLoading: qualityLoading } = useQuery<QualityFlagSummary>({
    queryKey: ["/api/admin/quality-flags/summary"],
    enabled: isSuperAdmin,
  });

  const { data: profileScanHealth, isLoading: profileScanLoading } = useQuery<ProfileScanHealth>({
    queryKey: ["/api/dashboard/profile-scan-health"],
    enabled: isSuperAdmin,
    refetchInterval: 60000,
  });

  const clearLockoutMutation = useMutation({
    mutationFn: async (entry: RateLimitEntry) => {
      await apiRequest("DELETE", `/api/admin/rate-limits/${entry.type}/${entry.key}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/rate-limits"] });
      toast({ title: "Lockout cleared", description: "The lockout has been removed." });
    },
    onError: () => {
      toast({ title: "Failed to clear lockout", description: "Please try again.", variant: "destructive" });
    },
  });

  const dismissIngestMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("PATCH", `/api/admin/inbound-emails/${id}/dismiss`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/ingest-health"] });
      toast({ title: "Entry dismissed", description: "It will no longer appear in this list." });
    },
    onError: () => {
      toast({ title: "Failed to dismiss", description: "Please try again.", variant: "destructive" });
    },
  });

  const dismissMismatchMutation = useMutation({
    mutationFn: async ({ reportId, reportType }: { reportId: string; reportType: "all_pets" | "interaction" }) => {
      await apiRequest("POST", "/api/dashboard/dismiss-mismatch", { reportId, reportType });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/ingest-health"] });
      toast({ title: "Discrepancy dismissed", description: "It will no longer appear in this list." });
    },
    onError: () => {
      toast({ title: "Failed to dismiss", description: "Please try again.", variant: "destructive" });
    },
  });

  const dismissScanAlertMutation = useMutation({
    mutationFn: async (alertCode: string) => {
      await apiRequest("POST", "/api/dashboard/profile-scan-health/dismiss", { alertCode });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/profile-scan-health"] });
      toast({ title: "Alert dismissed", description: "It will reappear if the same issue is detected in the next run." });
    },
    onError: () => {
      toast({ title: "Failed to dismiss", description: "Please try again.", variant: "destructive" });
    },
  });

  const lockoutCount = rateLimits?.lockedCount ?? 0;
  const pendingReports = pendingReportsData?.items ?? [];
  const pendingCount = pendingReportsData?.pagination?.total ?? 0;
  const diagnosticMismatches = ingestHealth?.diagnosticMismatches ?? [];
  const failedIngests = (ingestHealth?.summary.failed ?? 0) + (ingestHealth?.summary.needsReview ?? 0) + diagnosticMismatches.length;
  const showCalloutButtons = isAdmin || isSuperAdmin;

  // P16: quality flag tier counts
  const noteQualityCount = (qualityFlags?.noteQuality.emptyNote ?? 0)
    + (qualityFlags?.noteQuality.scoreOnly ?? 0)
    + (qualityFlags?.noteQuality.shortReport ?? 0);
  const dataIntegrityCount = (qualityFlags?.dataIntegrity.phantom ?? 0)
    + (qualityFlags?.dataIntegrity.contaminatedTail ?? 0)
    + (qualityFlags?.dataIntegrity.crossAnimalBleed ?? 0)
    + (qualityFlags?.dataIntegrity.unsuperseded ?? 0)
    + (qualityFlags?.dataIntegrity.brokenChain ?? 0)
    + (qualityFlags?.dataIntegrity.bloatedRawNote ?? 0);

  const healthLevel = deriveHealthLevel(systemInfo, systemInfoError, lockoutCount, failedIngests, pendingCount, dataIntegrityCount, profileScanHealth?.criticalCount ?? 0, profileScanHealth?.alertCount ?? 0);
  const HealthIcon = getHealthIcon(healthLevel);

  return (
    <>
      <div className="flex flex-col gap-3 transition-all w-full" data-testid="admin-callouts-bar" id="admin-callouts-bar">
        <div className={`flex flex-wrap items-center gap-3 border ${getHealthBorderColor(healthLevel)} rounded-md px-3 sm:px-4 py-3 min-w-0 ${getHealthCardBg(healthLevel)}${highlightClassName ? ` ${highlightClassName}` : ""}`}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 sm:gap-4 border-r pr-2 sm:pr-4 cursor-help min-w-0" data-testid="health-status-trigger" data-tour="dashboard-system-status">
                    <div className={`h-8 w-8 sm:h-10 sm:w-10 ${getHealthBgColor(healthLevel)} rounded-full flex items-center justify-center ${getHealthIconColor(healthLevel)} flex-shrink-0`}>
                      <HealthIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] sm:text-xs text-muted-foreground font-medium uppercase tracking-wider">System Health</p>
                      <h2 className={`text-sm sm:text-lg font-semibold leading-none mt-1 truncate ${healthLevel !== "operational" ? getHealthIconColor(healthLevel) : ""}`} data-testid="text-system-health">
                        {getHealthLabel(healthLevel)}
                      </h2>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs p-3">
                  <p className="text-xs font-semibold mb-2">Live system checks</p>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-xs">
                      {systemInfo?.database.healthy !== false
                        ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                        : <XCircle className="h-3.5 w-3.5 text-destructive flex-shrink-0" />}
                      <span>Database {systemInfo?.database.healthy !== false ? "healthy" : "unreachable"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      {systemInfo?.apiReachable !== false
                        ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                        : <XCircle className="h-3.5 w-3.5 text-destructive flex-shrink-0" />}
                      <span>API {systemInfo?.apiReachable !== false ? "reachable" : "unreachable"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      {lockoutCount === 0
                        ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                        : <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />}
                      <span>{lockoutCount === 0 ? "No active lockouts" : `${lockoutCount} active lockout${lockoutCount !== 1 ? "s" : ""}`}</span>
                    </div>
                    {isSuperAdmin && (
                      <div className="flex items-center gap-2 text-xs">
                        {failedIngests === 0
                          ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                          : <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />}
                        <span>{failedIngests === 0 ? "No ingest issues" : `${failedIngests} ingest issue${failedIngests !== 1 ? "s" : ""}`}</span>
                      </div>
                    )}
                    {isSuperAdmin && (
                      <div className="flex items-center gap-2 text-xs">
                        {noteQualityCount === 0
                          ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                          : <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />}
                        <span>{noteQualityCount === 0 ? "No note quality flags" : `${noteQualityCount} note quality flag${noteQualityCount !== 1 ? "s" : ""}`}</span>
                      </div>
                    )}
                    {isSuperAdmin && (
                      <div className="flex items-center gap-2 text-xs">
                        {dataIntegrityCount === 0
                          ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                          : <AlertTriangle className="h-3.5 w-3.5 text-destructive flex-shrink-0" />}
                        <span>{dataIntegrityCount === 0 ? "No data integrity flags" : `${dataIntegrityCount} data integrity flag${dataIntegrityCount !== 1 ? "s" : ""}`}</span>
                      </div>
                    )}
                    {isSuperAdmin && (
                      <div className="flex items-center gap-2 text-xs">
                        {(profileScanHealth?.alertCount ?? 0) === 0
                          ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                          : <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />}
                        <span>{(profileScanHealth?.alertCount ?? 0) === 0 ? "Profile scan healthy" : `${profileScanHealth!.alertCount} profile scan alert${profileScanHealth!.alertCount !== 1 ? "s" : ""}`}</span>
                      </div>
                    )}
                  </div>
                  {systemInfo?.timestamp && (
                    <p className="text-[10px] text-muted-foreground mt-2 pt-1.5 border-t">
                      Last checked {format(new Date(systemInfo.timestamp), "h:mm:ss a")}
                    </p>
                  )}
                </TooltipContent>
              </Tooltip>
              <div className="flex items-center gap-3 sm:gap-6 px-1 sm:px-4 min-w-0">
                <div className="flex flex-col min-w-0">
                  <span className="hidden sm:inline text-xs text-muted-foreground font-medium">Total Animals</span>
                  <span className="text-base sm:text-xl font-semibold flex items-center gap-1.5" data-testid="text-total-animals">
                    {totalAnimals}
                    <PawPrint className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 sm:hidden" />
                  </span>
                </div>
                {avgCapacityPercent !== null && (
                  <div className="flex flex-col min-w-0">
                    <span className="hidden sm:inline text-xs text-muted-foreground font-medium">Avg Capacity</span>
                    <span className="text-base sm:text-xl font-semibold flex items-center gap-1" data-testid="text-avg-capacity">
                      {avgCapacityPercent}%
                      <span className={`text-xs hidden sm:inline ${getCapacityColor(capacityStatus)}`}>{getCapacityLabel(capacityStatus)}</span>
                    </span>
                  </div>
                )}
              </div>
              {onCollapse && (
                <div className="ml-auto flex-shrink-0">
                  <ExpandCollapseButton
                    expanded={true}
                    onClick={onCollapse}
                    collapseLabel="Close"
                    testId="button-admin-banner-collapse"
                  />
                </div>
              )}
            </div>

            {showCalloutButtons && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full">
                {isAdmin && (
                  <Button
                    variant="outline"
                    onClick={() => { setLockoutSheetOpen(true); setReviewSheetOpen(false); setIngestSheetOpen(false); setProfileScanSheetOpen(false); }}
                    className="h-auto flex flex-col items-center sm:items-start gap-1.5 px-3 sm:px-4 py-2 sm:py-3 border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-950/30 text-blue-900 dark:text-blue-100 min-w-0"
                    data-testid="button-lockouts-callout"
                  >
                    <div className="hidden sm:flex items-center gap-2">
                      <Lock className="h-4 w-4 flex-shrink-0" />
                      <span className="text-[10px] font-semibold uppercase tracking-wider">User Lockouts</span>
                    </div>
                    {lockoutsLoading ? (
                      <Skeleton className="h-6 w-16" />
                    ) : (
                      <span className="flex items-center gap-2 text-lg sm:text-xl font-medium sm:font-semibold">
                        <Lock className="h-4 w-4 flex-shrink-0 sm:hidden" />
                        {lockoutCount}<span className="hidden sm:inline"> {lockoutCount === 1 ? "Lockout" : "Lockouts"}</span>
                      </span>
                    )}
                  </Button>
                )}

                {isAdmin && (
                  <Button
                    variant="outline"
                    onClick={() => { setReviewSheetOpen(true); setLockoutSheetOpen(false); setIngestSheetOpen(false); setProfileScanSheetOpen(false); }}
                    className="h-auto flex flex-col items-center sm:items-start gap-1.5 px-3 sm:px-4 py-2 sm:py-3 border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/30 text-amber-900 dark:text-amber-100 min-w-0"
                    data-testid="button-review-callout"
                  >
                    <div className="hidden sm:flex items-center gap-2">
                      <FileWarning className="h-4 w-4 flex-shrink-0" />
                      <span className="text-[10px] font-semibold uppercase tracking-wider">Needs Review</span>
                    </div>
                    {pendingLoading ? (
                      <Skeleton className="h-6 w-16" />
                    ) : (
                      <span className="flex items-center gap-2 text-lg sm:text-xl font-medium sm:font-semibold">
                        <FileWarning className="h-4 w-4 flex-shrink-0 sm:hidden" />
                        {pendingCount}<span className="hidden sm:inline"> Pending</span>
                      </span>
                    )}
                  </Button>
                )}

                {isSuperAdmin && (
                  <Button
                    variant="outline"
                    onClick={() => { setIngestSheetOpen(true); setLockoutSheetOpen(false); setReviewSheetOpen(false); setProfileScanSheetOpen(false); }}
                    className="h-auto flex flex-col items-center sm:items-start gap-1 px-3 sm:px-4 py-2 sm:py-3 border-red-200 dark:border-red-800 bg-red-50/60 dark:bg-red-950/30 text-red-900 dark:text-red-100 min-w-0"
                    data-testid="button-ingest-callout"
                  >
                    <div className="hidden sm:flex items-center gap-2">
                      <MailWarning className="h-4 w-4 flex-shrink-0" />
                      <span className="text-[10px] font-semibold uppercase tracking-wider">Ingest Issues</span>
                    </div>
                    {ingestLoading || qualityLoading ? (
                      <Skeleton className="h-6 w-16" />
                    ) : (
                      <>
                        <span className="flex items-center gap-2 text-lg sm:text-xl font-medium sm:font-semibold">
                          <MailWarning className="h-4 w-4 flex-shrink-0 sm:hidden" />
                          {failedIngests}<span className="hidden sm:inline"> Issues</span>
                        </span>
                        {(noteQualityCount > 0 || dataIntegrityCount > 0) && (
                          <span className="hidden sm:flex items-center gap-1 text-[10px] text-red-700/70 dark:text-red-300/70 flex-wrap">
                            {noteQualityCount > 0 && <span>{noteQualityCount} note quality</span>}
                            {noteQualityCount > 0 && dataIntegrityCount > 0 && <span aria-hidden>·</span>}
                            {dataIntegrityCount > 0 && <span>{dataIntegrityCount} data integrity</span>}
                          </span>
                        )}
                      </>
                    )}
                  </Button>
                )}

                {isSuperAdmin && (
                  <Button
                    variant="outline"
                    onClick={() => { setProfileScanSheetOpen(true); setLockoutSheetOpen(false); setReviewSheetOpen(false); setIngestSheetOpen(false); }}
                    className="h-auto flex flex-col items-center sm:items-start gap-1 px-3 sm:px-4 py-2 sm:py-3 border-violet-200 dark:border-violet-800 bg-violet-50/60 dark:bg-violet-950/30 text-violet-900 dark:text-violet-100 min-w-0"
                    data-testid="button-profile-scan-callout"
                  >
                    <div className="hidden sm:flex items-center gap-2">
                      <Camera className="h-4 w-4 flex-shrink-0" />
                      <span className="text-[10px] font-semibold uppercase tracking-wider">Profile Scan</span>
                    </div>
                    {profileScanLoading ? (
                      <Skeleton className="h-6 w-16" />
                    ) : (
                      <>
                        <span className="flex items-center gap-2 text-lg sm:text-xl font-medium sm:font-semibold">
                          <Camera className="h-4 w-4 flex-shrink-0 sm:hidden" />
                          {profileScanHealth?.alertCount ?? 0}<span className="hidden sm:inline"> Alert{(profileScanHealth?.alertCount ?? 0) !== 1 ? "s" : ""}</span>
                        </span>
                        {(profileScanHealth?.criticalCount ?? 0) > 0 && (
                          <span className="hidden sm:flex items-center gap-1 text-[10px] text-violet-700/70 dark:text-violet-300/70">
                            <span>{profileScanHealth!.criticalCount} critical</span>
                          </span>
                        )}
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>

      <Sheet open={lockoutSheetOpen} onOpenChange={setLockoutSheetOpen}>
        <SheetContent className="w-full sm:w-[480px] flex flex-col overflow-hidden">
          <SheetHeader className="flex-shrink-0">
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <SheetTitle>Active User Lockouts</SheetTitle>
            </div>
            <SheetDescription>
              {lockoutCount} {lockoutCount === 1 ? "account is" : "accounts are"} currently locked due to failed authentication attempts.
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto mt-6 space-y-3 min-h-0">
            {lockoutsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
              </div>
            ) : rateLimits?.lockedEntries.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground" data-testid="text-no-lockouts">
                <p className="text-sm">No active lockouts</p>
              </div>
            ) : (
              rateLimits?.lockedEntries.map((entry, idx) => (
                <div key={`${entry.type}-${entry.key}`} className="border rounded-md p-3.5 space-y-2" data-testid={`card-lockout-${idx}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] font-medium">{entry.type}</Badge>
                      <span className="text-sm font-bold">{entry.key}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs gap-1"
                      onClick={() => clearLockoutMutation.mutate(entry)}
                      disabled={clearLockoutMutation.isPending}
                      data-testid={`button-clear-lockout-${idx}`}
                    >
                      <Unlock className="h-3 w-3" />
                      Clear
                    </Button>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span className="text-muted-foreground">
                      Locked until {format(new Date(entry.lockedUntil), "h:mm a")}
                    </span>
                    <span className="font-medium text-blue-600 dark:text-blue-400">
                      Expires in {formatRemainingTime(entry.remainingSeconds)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="flex-shrink-0 pt-4 border-t">
            <Button variant="outline" className="w-full text-xs" asChild>
              <Link href="/admin/settings?tab=system" data-testid="link-lockout-management">
                View Full Lockout Management <ExternalLink className="ml-1.5 h-3 w-3" />
              </Link>
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={reviewSheetOpen} onOpenChange={setReviewSheetOpen}>
        <SheetContent className="w-full sm:w-[480px] flex flex-col overflow-hidden">
          <SheetHeader className="flex-shrink-0">
            <div className="flex items-center gap-2">
              <FileWarning className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              <SheetTitle>Reports Needing Review</SheetTitle>
            </div>
            <SheetDescription>
              {pendingCount} {pendingCount === 1 ? "report" : "reports"} failed automated validation and {pendingCount === 1 ? "requires" : "require"} manual review.
              {pendingReports.length < pendingCount && (
                <span className="block mt-0.5">
                  Showing {pendingReports.length} of {pendingCount} oldest — see the review queue for the full list.
                </span>
              )}
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto mt-6 space-y-3 min-h-0">
            {pendingLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-28 w-full" />)}
              </div>
            ) : pendingReports.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground" data-testid="text-no-pending-reports">
                <p className="text-sm">No reports needing review</p>
              </div>
            ) : (
              pendingReports.map((report) => (
                <div key={report.id} className="border rounded-md p-3.5 space-y-2.5" data-testid={`card-pending-report-${report.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`text-[10px] font-medium ${
                          report.sourceReportType === "all_pets"
                            ? "border-primary/30 text-primary"
                            : "border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300"
                        }`}>
                          {getReportTypeLabel(report.sourceReportType)}
                        </Badge>
                        {report.parsedData?.location && (
                          <span className="text-sm font-medium" data-testid={`text-location-${report.id}`}>{report.parsedData.location}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {report.parsedData?.reportDate && (
                          <span data-testid={`text-report-date-${report.id}`}>
                            Report: {format(new Date(report.parsedData.reportDate + "T00:00:00"), "MMM d, yyyy")}
                          </span>
                        )}
                        <span data-testid={`text-received-${report.id}`}>
                          Received: {format(new Date(report.createdAt), "MMM d, h:mm a")}
                        </span>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="text-xs shrink-0" asChild>
                      <Link href={getReviewUrl(report)} data-testid={`button-review-${report.id}`}>
                        Review
                      </Link>
                    </Button>
                  </div>
                  <div className="flex items-center flex-wrap gap-1.5">
                    {(() => {
                      const parsed = parseEmailIngestHoldReason(report.reviewReason);
                      if (parsed.isEmailIngestHold) {
                        return (
                          <Badge variant="outline" className="shrink-0 text-[10px] bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800" data-testid={`badge-reason-${report.id}`}>
                            Email review
                          </Badge>
                        );
                      }
                      const scoreDiag = parseInteractionDiagnostic(report.reviewReason);
                      if (scoreDiag.isScoreDiagnostic) {
                        return (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className="shrink-0 text-[10px] bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-400 dark:border-blue-800 cursor-help" data-testid={`badge-reason-${report.id}`}>
                                {scoreDiag.count} missing score{scoreDiag.count !== 1 ? "s" : ""}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-sm">
                              <p className="text-xs font-medium mb-1">Animals without scores:</p>
                              <p className="text-[10px] text-muted-foreground">{scoreDiag.animalIds.join(", ")}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      }
                      if (report.reviewReason) {
                        return <span className="text-xs text-muted-foreground line-clamp-1 break-all" data-testid={`text-reason-${report.id}`}>{report.reviewReason}</span>;
                      }
                      return null;
                    })()}
                  </div>
                </div>
              ))
            )}
            {!pendingLoading && pendingReports.length < pendingCount && pendingReports.length > 0 && (
              <p className="text-xs text-muted-foreground text-center py-2" data-testid="text-pending-truncated">
                Showing {pendingReports.length} of {pendingCount} oldest reports
              </p>
            )}
          </div>
          <div className="flex-shrink-0 pt-4 border-t">
            <Button variant="outline" className="w-full text-xs" asChild>
              <Link href="/reports/review" data-testid="link-review-queue">
                View Full Review Queue <ExternalLink className="ml-1.5 h-3 w-3" />
              </Link>
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={ingestSheetOpen} onOpenChange={setIngestSheetOpen}>
        <SheetContent className="w-full sm:w-[480px] flex flex-col overflow-hidden">
          <SheetHeader className="flex-shrink-0">
            <div className="flex items-center gap-2">
              <MailWarning className="h-5 w-5 text-destructive" />
              <SheetTitle>Ingest Issues</SheetTitle>
            </div>
            <SheetDescription>
              {failedIngests} ingest {failedIngests === 1 ? "issue" : "issues"} in the last {ingestHealth?.lookbackDays ?? 7} days
              {(noteQualityCount > 0 || dataIntegrityCount > 0) && (
                <>, plus {noteQualityCount + dataIntegrityCount} quality {(noteQualityCount + dataIntegrityCount) === 1 ? "flag" : "flags"}</>
              )}.
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto mt-6 min-h-0">
            <div className="grid grid-cols-3 gap-3">
              <div className="border rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Processed</div>
                {ingestLoading ? (
                  <Skeleton className="h-8 w-12" />
                ) : (
                  <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400" data-testid="text-ingest-processed-count">
                    {ingestHealth?.summary.processed?.toLocaleString() ?? 0}
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground">Last {ingestHealth?.lookbackDays ?? 7} days</div>
              </div>
              <div className="border rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Failed</div>
                {ingestLoading ? (
                  <Skeleton className="h-8 w-8" />
                ) : (
                  <div className="text-2xl font-bold text-destructive" data-testid="text-ingest-failed-count">
                    {ingestHealth?.summary.failed ?? 0}
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground">Hard errors</div>
              </div>
              <div className="border rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Review</div>
                {ingestLoading ? (
                  <Skeleton className="h-8 w-8" />
                ) : (
                  <div className="text-2xl font-bold text-amber-600 dark:text-amber-400" data-testid="text-ingest-review-count">
                    {ingestHealth?.summary.needsReview ?? 0}
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground">Needs review</div>
              </div>
            </div>
            {!ingestLoading && ingestHealth && ingestHealth.failures.length > 0 && (
              <div className="mt-4 space-y-2">
                {ingestHealth.failures.slice(0, 10).map((failure) => {
                  const isExpanded = expandedFailureId === failure.id;
                  const isNeedsReview = failure.processingStatus === "needs_review";
                  return (
                    <div key={failure.id} className="border rounded-md" data-testid={`card-ingest-failure-${failure.id}`}>
                      <div className="p-3 flex items-start gap-2.5">
                        <Mail className="h-4 w-4 text-destructive/60 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                            <Badge
                              variant="outline"
                              className={`text-[10px] px-1.5 py-0 ${isNeedsReview ? "border-amber-400/40 text-amber-600 dark:text-amber-400" : "border-destructive/40 text-destructive"}`}
                            >
                              {isNeedsReview ? "Needs Review" : "Failed"}
                            </Badge>
                            <span className="text-[10px] text-muted-foreground">
                              {format(new Date(failure.receivedAt), "MMM d")}
                            </span>
                          </div>
                          <p className={`text-sm font-medium ${isExpanded ? "break-words whitespace-pre-wrap" : "truncate"}`}>
                            {failure.processingError || "Processing failed"}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">
                            From: {failure.fromAddress || "Unknown"}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setExpandedFailureId(isExpanded ? null : failure.id)}
                            data-testid={`button-expand-failure-${failure.id}`}
                          >
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-muted-foreground"
                            onClick={() => dismissIngestMutation.mutate(failure.id)}
                            disabled={dismissIngestMutation.isPending}
                            data-testid={`button-dismiss-failure-${failure.id}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-3 pb-3 pt-0 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2 text-xs w-full"
                            onClick={() => {
                              setIngestSheetOpen(false);
                              setLocation(`/admin/settings?tab=email-ingest&highlight=${failure.id}&status=${failure.processingStatus}`);
                            }}
                            data-testid={`button-view-ingest-log-${failure.id}`}
                          >
                            View in Ingest Log <ExternalLink className="ml-1.5 h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {!ingestLoading && diagnosticMismatches.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-0.5">Count Discrepancies</p>
                {diagnosticMismatches.map((m) => (
                  <div key={m.reportId} className="border rounded-md p-3.5 space-y-2" data-testid={`card-diag-mismatch-${m.reportId}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge
                            variant="outline"
                            className={`text-[10px] font-medium ${
                              m.reportType === "all_pets"
                                ? "border-primary/30 text-primary"
                                : "border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300"
                            }`}
                          >
                            {m.reportType === "all_pets" ? "All Pets Report" : "Daily Interactions"}
                          </Badge>
                          {m.location && (
                            <span className="text-sm font-medium">{m.location}</span>
                          )}
                        </div>
                        {m.reportDate && (
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(m.reportDate + "T00:00:00"), "MMM d, yyyy")}
                          </p>
                        )}
                        <ul className="space-y-0.5">
                          {(Array.isArray(m.mismatches) ? m.mismatches : []).map((msg, i) => (
                            <li key={i} className="text-xs text-muted-foreground font-mono">{msg}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          asChild
                        >
                          <Link
                            href={m.reportType === "all_pets"
                              ? `/reports/${m.reportId}?drawer=diagnostics`
                              : `/reports/behavior/${m.reportId}?drawer=diagnostics`}
                            data-testid={`button-view-mismatch-${m.reportId}`}
                            onClick={() => setIngestSheetOpen(false)}
                          >
                            View Report
                          </Link>
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-muted-foreground"
                          onClick={() => dismissMismatchMutation.mutate({ reportId: m.reportId, reportType: m.reportType })}
                          disabled={dismissMismatchMutation.isPending}
                          data-testid={`button-dismiss-mismatch-${m.reportId}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!ingestLoading && ingestHealth && ingestHealth.failures.length === 0 && diagnosticMismatches.length === 0 && (
              <div className="mt-4 p-6 text-center text-muted-foreground" data-testid="text-no-ingest-failures">
                <p className="text-sm">No ingest failures or count discrepancies in this period</p>
              </div>
            )}
            {/* P16: Quality flag breakdown — always below the ingest failures block */}
            {!qualityLoading && (noteQualityCount > 0 || dataIntegrityCount > 0) && (
              <div className="mt-4 space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-0.5">Quality Flags</p>
                <p className="text-xs text-muted-foreground px-0.5">
                  Interaction records with detected note or data problems. These do not block operations but should be reviewed.
                </p>
                <div className="space-y-3">
                  <button
                    className="border rounded-md p-3 space-y-1 text-left hover-elevate active-elevate-2 w-full"
                    onClick={() => { setIngestSheetOpen(false); setLocation("/admin/settings?tab=email-ingest&qualityFlag=note-quality"); }}
                    data-testid="button-note-quality-card"
                  >
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Note Quality</div>
                    <div className="text-2xl font-bold text-amber-600 dark:text-amber-400 flex items-center gap-1.5" data-testid="text-note-quality-count">
                      {noteQualityCount}
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </div>
                    <p className="text-[10px] text-muted-foreground leading-tight">Notes that may lack meaningful behavior content</p>
                    <div className="text-[10px] text-muted-foreground pt-1 space-y-0.5 border-t">
                      {(qualityFlags?.noteQuality.emptyNote ?? 0) > 0 && <div>Empty note: {qualityFlags!.noteQuality.emptyNote}</div>}
                      {(qualityFlags?.noteQuality.scoreOnly ?? 0) > 0 && <div>Score-only: {qualityFlags!.noteQuality.scoreOnly}</div>}
                      {(qualityFlags?.noteQuality.shortReport ?? 0) > 0 && <div>Short report: {qualityFlags!.noteQuality.shortReport}</div>}
                    </div>
                  </button>
                  <div
                    className="border rounded-md p-3 space-y-1 text-left w-full"
                    data-testid="card-data-integrity"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Data Integrity</span>
                        <button
                          className="flex items-center gap-1 hover:opacity-70 transition-opacity"
                          onClick={() => { setIngestSheetOpen(false); setLocation("/admin/settings?tab=email-ingest&qualityFlag=data-integrity"); }}
                          data-testid="button-data-integrity-navigate"
                        >
                          <span className={`text-2xl font-bold leading-none ${dataIntegrityCount > 0 ? "text-destructive" : "text-muted-foreground"}`} data-testid="text-data-integrity-count">
                            {dataIntegrityCount}
                          </span>
                          <ExternalLink className="h-3 w-3 text-muted-foreground ml-0.5" />
                        </button>
                      </div>
                      <ExpandCollapseButton
                        expanded={dataIntegrityExpanded}
                        onClick={() => setDataIntegrityExpanded((v) => !v)}
                        expandLabel="Details"
                        collapseLabel="Hide"
                        testId="button-data-integrity-expand"
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground leading-tight">Records with accuracy or pipeline issues</p>
                    {dataIntegrityExpanded && (
                      <div className="pt-1.5 border-t space-y-2">
                        {DATA_INTEGRITY_ITEMS.map(({ key, label, description }) => {
                          const count = qualityFlags?.dataIntegrity[key] ?? 0;
                          return (
                            <div key={key} className="flex items-start gap-2">
                              <span
                                className={`text-sm font-semibold tabular-nums w-4 flex-shrink-0 mt-0.5 ${count > 0 ? "text-destructive" : "text-emerald-600 dark:text-emerald-400"}`}
                                data-testid={`text-integrity-count-${key}`}
                              >
                                {count}
                              </span>
                              <div className="min-w-0">
                                <div className={`text-[10px] font-medium leading-tight ${count > 0 ? "text-foreground" : "text-muted-foreground"}`}>
                                  {label}
                                </div>
                                <div className="text-[10px] text-muted-foreground leading-tight opacity-80">
                                  {description}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex-shrink-0 pt-4 border-t">
            <Button variant="outline" className="w-full text-xs" asChild>
              <Link href="/admin/settings?tab=email-ingest" data-testid="link-ingest-dashboard">
                View Ingest Dashboard <ExternalLink className="ml-1.5 h-3 w-3" />
              </Link>
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={profileScanSheetOpen} onOpenChange={setProfileScanSheetOpen}>
        <SheetContent className="w-full sm:w-[480px] flex flex-col overflow-hidden">
          <SheetHeader className="flex-shrink-0">
            <div className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-violet-600 dark:text-violet-400" />
              <SheetTitle>Profile Scan Health</SheetTitle>
            </div>
            <SheetDescription>
              Daily scrape status and alert summary for mspca.org animal profiles.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto mt-4 space-y-4 min-h-0">
            {profileScanLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-5 w-48" />
                <div className="grid grid-cols-3 gap-3">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
                <Skeleton className="h-5 w-32" />
                {[1, 2].map(i => <Skeleton key={i} className="h-16 w-full" />)}
              </div>
            ) : (
              <>
                {/* Last run summary line */}
                <p className="text-xs text-muted-foreground">
                  {profileScanHealth?.lastRunAt
                    ? <>Last run: <span className="text-foreground font-medium">{format(new Date(profileScanHealth.lastRunAt), "MMM d, h:mm a")}</span></>
                    : "No runs recorded"}
                </p>

                {/* Stats grid */}
                {profileScanHealth?.lastRunAnimalCount != null && (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="border rounded-md p-3 text-center">
                      <p className="text-xl font-bold tabular-nums" data-testid="text-scan-animals">{profileScanHealth.lastRunAnimalCount}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">Animals</p>
                    </div>
                    <div className="border rounded-md p-3 text-center">
                      <p className={`text-xl font-bold tabular-nums ${(profileScanHealth.lastRunHttp200 ?? 0) > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`} data-testid="text-scan-profiles">{profileScanHealth.lastRunHttp200 ?? 0}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">Profiles Found</p>
                    </div>
                    <div className="border rounded-md p-3 text-center">
                      <p className={`text-xl font-bold tabular-nums ${(profileScanHealth.lastRunPhotoFoundCount ?? 0) > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`} data-testid="text-scan-photos">{profileScanHealth.lastRunPhotoFoundCount ?? 0}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">Photos</p>
                    </div>
                  </div>
                )}

                {/* Bio / error detail line */}
                {profileScanHealth?.lastRunHttp200 != null && (
                  <p className="text-[10px] text-muted-foreground -mt-1">
                    <span className="font-medium text-foreground">{profileScanHealth.lastRunBioFoundCount ?? 0}</span> bios found
                    {(profileScanHealth.lastRunHttp404 ?? 0) > 0 && (
                      <> &middot; <span className="font-medium text-foreground">{profileScanHealth.lastRunHttp404}</span> not found (404)</>
                    )}
                    {(profileScanHealth.lastRunErrorCount ?? 0) > 0 && (
                      <> &middot; <span className="font-medium text-destructive">{profileScanHealth.lastRunErrorCount}</span> HTTP errors</>
                    )}
                  </p>
                )}

                {/* Alerts section */}
                <div className="space-y-2 pt-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Active Alerts</p>
                  {(profileScanHealth?.alerts.length ?? 0) === 0 ? (
                    <div className="mt-2 p-6 text-center text-muted-foreground" data-testid="text-no-scan-alerts">
                      <CheckCircle2 className="h-6 w-6 text-emerald-500 mx-auto mb-2" />
                      <p className="text-sm">All checks passed</p>
                    </div>
                  ) : (
                    profileScanHealth!.alerts.map((alert, idx) => (
                      <div
                        key={alert.code}
                        className={`border rounded-md p-3.5 ${alert.severity === "critical" ? "border-destructive/40 bg-destructive/5" : "border-amber-400/40 bg-amber-50/40 dark:bg-amber-950/20"}`}
                        data-testid={`card-scan-alert-${idx}`}
                      >
                        <div className="flex items-start gap-2">
                          {alert.severity === "critical"
                            ? <XCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                            : <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge
                                variant="outline"
                                className={`text-[10px] ${alert.severity === "critical" ? "border-destructive/40 text-destructive" : "border-amber-400/60 text-amber-700 dark:text-amber-400"}`}
                              >
                                {alert.severity}
                              </Badge>
                            </div>
                            <p className="text-sm font-medium mt-1 leading-snug">{alert.message}</p>
                            {alert.detail && (
                              <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug">{alert.detail}</p>
                            )}
                          </div>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-muted-foreground flex-shrink-0 -mt-0.5"
                            onClick={() => dismissScanAlertMutation.mutate(alert.code)}
                            disabled={dismissScanAlertMutation.isPending}
                            data-testid={`button-dismiss-scan-alert-${alert.code}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
